import React from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { getSensorDepthList } from "../utils/sensorDepths";

const safeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const moisturePosition = (value) => {
  const parsed = safeNumber(value);
  if (parsed === null) return 0;
  return Math.max(0, Math.min(100, parsed));
};

const formatReading = (value) => {
  const parsed = safeNumber(value);
  return parsed === null ? "—" : parsed.toFixed(1);
};

const AllFields = ({ filteredFields = [], sensorsData = [], crops = [], userId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const routeToField = (fieldUid) => {
    if (!fieldUid) return;
    localStorage.setItem("Field", fieldUid);
    navigate(`/Fields/${fieldUid}`);
  };

  const batteryIcon = (charge) => {
    const level = safeNumber(charge);
    if (level === null) return "fa-battery-empty";
    if (level >= 85) return "fa-battery-full";
    if (level >= 60) return "fa-battery-three-quarters";
    if (level >= 35) return "fa-battery-half";
    if (level >= 10) return "fa-battery-quarter";
    return "fa-battery-empty";
  };

  if (!filteredFields.length) {
    return (
      <div className="overview-fields-empty">
        <span><i className="fas fa-map-marked-alt" /></span>
        <h3>{t("no_fields")}</h3>
        <p>{t("overview_fields_subtitle", { defaultValue: "Search, filter and open your fields from one place." })}</p>
      </div>
    );
  }

  return (
    <Row className="overview-fields-grid">
      {filteredFields.map((field) => {
        const fieldCrop = crops.find((crop) => crop.fieldId === field.id);
        const fieldSensors = Array.isArray(field.sensors)
          ? field.sensors.filter(
              (sensor) => !userId || String(sensor.user_id) === String(userId)
            )
          : [];

        return (
          <Col xl="4" lg="6" md="12" key={field.uid} className="overview-field-column">
            <article className="overview-field-card">
              <header className="overview-field-header">
                <button type="button" className="overview-field-title" onClick={() => routeToField(field.uid)}>
                  <span className="overview-field-icon"><i className="fas fa-map-marker-alt" /></span>
                  <span>
                    <small>{t("field")}</small>
                    <strong>{field.name}</strong>
                  </span>
                </button>
                <div className="overview-field-header-actions">
                  <span className={`overview-field-status is-${String(field.status || "unknown").toLowerCase().replace(/\s+/g, "-")}`}>
                    {field.status ? t(field.status) : "—"}
                  </span>
                  <button type="button" className="overview-field-open" onClick={() => routeToField(field.uid)} aria-label={field.name}>
                    <i className="fas fa-arrow-right" />
                  </button>
                </div>
              </header>

              <div className="overview-field-crop">
                <div className="overview-field-crop-visual">
                  {fieldCrop?.photo ? (
                    <img
                      src={`${process.env.REACT_APP_BASE_URL}/docs/img/crop/${encodeURIComponent(fieldCrop.photo)}`}
                      alt={fieldCrop.type || t("crop_type")}
                    />
                  ) : (
                    <i className={`fas ${fieldCrop ? "fa-seedling" : "fa-leaf"}`} />
                  )}
                </div>
                <div>
                  <small>{t("crop_type")}</small>
                  <strong>{fieldCrop?.type || t("no_crop_data")}</strong>
                </div>
                <span className="overview-field-sensor-count">
                  <i className="fas fa-broadcast-tower" />
                  {fieldSensors.length}
                </span>
              </div>

              <div className="overview-field-sensors">
                {fieldSensors.length === 0 ? (
                  <div className="overview-field-no-sensor">
                    <i className="fas fa-satellite-dish" />
                    <span>{t("No_Sensors_Message")}</span>
                  </div>
                ) : (
                  fieldSensors.map((sensor) => {
                    const reading = sensorsData.find((data) => data.code === sensor.code);
                    const signal = safeNumber(reading?.signal);
                    const charge = safeNumber(reading?.charge);
                    const levels = [reading?.mv1, reading?.mv2, reading?.mv3];
                    const depthValues = getSensorDepthList(sensor);
                    const lastReading = reading?.time
                      ? moment(reading.time).locale("fr").format("L, LT")
                      : "—";

                    return (
                      <section className="overview-sensor-card" key={sensor.uid || sensor.id || sensor.code}>
                        <div className="overview-sensor-heading">
                          <div>
                            <span className="overview-sensor-live-dot" />
                            <strong>{sensor.code}</strong>
                          </div>
                          <div className="overview-sensor-health">
                            <span title={t("signal")}><i className="fas fa-signal" />{signal === null ? "—" : `${Math.round(signal)}%`}</span>
                            <span title={t("battery")}><i className={`fas ${batteryIcon(charge)}`} />{charge === null ? "—" : `${Math.round(charge)}%`}</span>
                          </div>
                        </div>

                        <div className="overview-moisture-levels">
                          {levels.map((level, index) => (
                            <div className="overview-moisture-row" key={`${sensor.code}-${index}`}>
                              <span>{depthValues[index]} cm</span>
                              <div className="overview-moisture-track">
                                <div className="overview-moisture-gradient" />
                                {safeNumber(level) !== null && (
                                  <span className="overview-moisture-marker" style={{ left: `${moisturePosition(level)}%` }}>
                                    <i className="fas fa-tint" />
                                  </span>
                                )}
                              </div>
                              <strong>{formatReading(level)}%</strong>
                            </div>
                          ))}
                        </div>

                        <div className="overview-moisture-legend">
                          <span>{t("Critical")}</span>
                          <span>{t("Optimal")}</span>
                          <span>{t("Full")}</span>
                        </div>

                        <footer className="overview-sensor-footer">
                          <span><i className="far fa-clock" />{t("last_reading")}</span>
                          <strong>{lastReading}</strong>
                        </footer>
                      </section>
                    );
                  })
                )}
              </div>
            </article>
          </Col>
        );
      })}
    </Row>
  );
};

export default AllFields;
